import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import {
  ArrowLeft, Link2, Unlink, RefreshCw, CheckCircle, XCircle,
  FileText, Receipt, Landmark, Clock, ChevronDown, ChevronUp,
  Building2, Zap, TrendingUp, TrendingDown, DollarSign, AlertTriangle,
  ExternalLink, Loader, Trash2, HardDrive, ShieldAlert, Pause, Play, Ban,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const PROVIDER_ICONS = {
  quickbooks: 'Q',
  xero: 'X',
  zoho_books: 'Z',
  freshbooks: 'F',
  wave: 'W',
  dynamics365: 'D',
  netsuite: 'N',
  sage_intacct: 'S',
};

const txt = {
  nl: {
    title: 'Boekhoudsoftware Koppelingen',
    subtitle: 'Koppel je boekhoudsoftware om automatisch financiele data te importeren',
    back: 'Terug',
    mustHave: 'Populaire Koppelingen',
    enterprise: 'Enterprise Koppelingen',
    connect: 'Koppelen',
    disconnect: 'Ontkoppelen',
    sync: 'Synchroniseren',
    syncing: 'Synchroniseren...',
    connected: 'Actief',
    disconnected: 'Niet gekoppeld',
    paused: 'Gepauzeerd',
    disabled: 'Uitgeschakeld',
    lastSync: 'Laatste sync',
    never: 'Nog niet gesynchroniseerd',
    invoices: 'Facturen',
    expenses: 'Uitgaven',
    revenue: 'Omzet',
    netProfit: 'Netto winst',
    bankBalance: 'Banksaldo',
    totalExpenses: 'Totale uitgaven',
    months: 'maanden',
    records: 'records',
    connectSuccess: 'Succesvol gekoppeld!',
    disconnectSuccess: 'Ontkoppeld',
    syncSuccess: 'Synchronisatie voltooid',
    noData: 'Geen data beschikbaar. Synchroniseer eerst.',
    summaryTitle: 'Financieel Overzicht',
    connectedProviders: 'Gekoppelde bronnen',
    viewData: 'Bekijk data',
    hideData: 'Verberg data',
    features: 'Functies',
    category: { invoices: 'Facturen', expenses: 'Uitgaven', bank_feeds: 'Bankfeeds', reports: 'Rapporten', payroll: 'Salarisadmin', inventory: 'Voorraad', time_tracking: 'Tijdregistratie', projects: 'Projecten', erp: 'ERP', crm: 'CRM', consolidation: 'Consolidatie', analytics: 'Analyse' },
    profitMargin: 'Winstmarge',
    totalRecords: 'Totale records',
    paid: 'Betaald',
    pending: 'Openstaand',
    overdue: 'Achterstallig',
    deleteData: 'Verwijder data',
    deleteAllData: 'Alle data verwijderen',
    deleteConfirm: 'Weet je zeker dat je alle data wilt verwijderen?',
    deleteProviderConfirm: 'Data voor deze provider verwijderen?',
    deleteSuccess: 'Data verwijderd',
    storage: 'Opslag',
    storageUsed: 'gebruikt',
    autoCleanup: 'Auto-opschoning na',
    hours: 'uur',
    cancel: 'Annuleren',
    confirm: 'Bevestigen',
    pause: 'Pauzeren',
    resume: 'Hervatten',
    disable: 'Uitschakelen',
    activate: 'Activeren',
    ephemeralNote: 'Data is tijdelijk (4 uur) en wordt automatisch opgeschoond',
  },
  en: {
    title: 'Accounting Software Integrations',
    subtitle: 'Connect your accounting software to automatically import financial data',
    back: 'Back',
    mustHave: 'Popular Integrations',
    enterprise: 'Enterprise Integrations',
    connect: 'Connect',
    disconnect: 'Disconnect',
    sync: 'Sync Data',
    syncing: 'Syncing...',
    connected: 'Active',
    disconnected: 'Not connected',
    paused: 'Paused',
    disabled: 'Disabled',
    lastSync: 'Last sync',
    never: 'Not synced yet.',
    invoices: 'Invoices',
    expenses: 'Expenses',
    revenue: 'Revenue',
    netProfit: 'Net Profit',
    bankBalance: 'Bank Balance',
    totalExpenses: 'Total Expenses',
    months: 'months',
    records: 'records',
    connectSuccess: 'Successfully connected!',
    disconnectSuccess: 'Disconnected',
    syncSuccess: 'Sync completed',
    noData: 'No data available. Sync first.',
    summaryTitle: 'Financial Overview',
    connectedProviders: 'Connected sources',
    viewData: 'View data',
    hideData: 'Hide data',
    features: 'Features',
    category: { invoices: 'Invoices', expenses: 'Expenses', bank_feeds: 'Bank Feeds', reports: 'Reports', payroll: 'Payroll', inventory: 'Inventory', time_tracking: 'Time Tracking', projects: 'Projects', erp: 'ERP', crm: 'CRM', consolidation: 'Consolidation', analytics: 'Analytics' },
    profitMargin: 'Profit Margin',
    totalRecords: 'Total records',
    paid: 'Paid',
    pending: 'Pending',
    overdue: 'Overdue',
    deleteData: 'Delete data',
    deleteAllData: 'Delete all data',
    deleteConfirm: 'Are you sure you want to delete all data?',
    deleteProviderConfirm: 'Delete data for this provider?',
    deleteSuccess: 'Data deleted',
    storage: 'Storage',
    storageUsed: 'used',
    autoCleanup: 'Auto-cleanup after',
    hours: 'hours',
    cancel: 'Cancel',
    confirm: 'Confirm',
    pause: 'Pause',
    resume: 'Resume',
    disable: 'Disable',
    activate: 'Activate',
    ephemeralNote: 'Data is temporary (4 hours) and auto-cleaned',
  },
};

export default function AccountingIntegrations() {
  const { token } = useAuth();
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const t = txt[lang] || txt.en;
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [providers, setProviders] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [expandedProvider, setExpandedProvider] = useState(null);
  const [providerData, setProviderData] = useState({});
  const [toast, setToast] = useState(null);
  const [storage, setStorage] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // null | 'all' | providerId

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchAll = useCallback(async () => {
    try {
      const h = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      const [provRes, sumRes, storRes] = await Promise.all([
        fetch(`${API}/accounting/providers`, { headers: h }),
        fetch(`${API}/accounting/summary`, { headers: h }),
        fetch(`${API}/accounting/storage`, { headers: h }),
      ]);
      const provData = await provRes.json();
      const sumData = await sumRes.json();
      const storData = await storRes.json();
      setProviders(provData.providers || []);
      setSummary(sumData);
      setStorage(storData);
    } catch (e) {
      console.error('Failed to fetch accounting data:', e);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleConnect = async (providerId) => {
    setActionLoading(prev => ({ ...prev, [providerId]: 'connect' }));
    try {
      const res = await fetch(`${API}/accounting/connect`, {
        method: 'POST', headers, body: JSON.stringify({ provider_id: providerId }),
      });
      if (res.ok) {
        showToast(t.connectSuccess);
        await fetchAll();
      }
    } catch (e) { showToast(e.message, 'error'); }
    setActionLoading(prev => ({ ...prev, [providerId]: null }));
  };

  const handleDisconnect = async (providerId) => {
    setActionLoading(prev => ({ ...prev, [providerId]: 'disconnect' }));
    try {
      const res = await fetch(`${API}/accounting/disconnect`, {
        method: 'POST', headers, body: JSON.stringify({ provider_id: providerId }),
      });
      if (res.ok) {
        showToast(t.disconnectSuccess);
        setExpandedProvider(null);
        setProviderData(prev => { const n = { ...prev }; delete n[providerId]; return n; });
        await fetchAll();
      }
    } catch (e) { showToast(e.message, 'error'); }
    setActionLoading(prev => ({ ...prev, [providerId]: null }));
  };

  const handleSync = async (providerId) => {
    setActionLoading(prev => ({ ...prev, [providerId]: 'sync' }));
    try {
      const res = await fetch(`${API}/accounting/sync`, {
        method: 'POST', headers, body: JSON.stringify({ provider_id: providerId }),
      });
      if (res.ok) {
        const data = await res.json();
        showToast(`${t.syncSuccess} — ${data.records} ${t.records}`);
        await fetchAll();
        // Auto-load synced data
        const dataRes = await fetch(`${API}/accounting/data/${providerId}`, { headers });
        if (dataRes.ok) {
          const d = await dataRes.json();
          setProviderData(prev => ({ ...prev, [providerId]: d }));
        }
      }
    } catch (e) { showToast(e.message, 'error'); }
    setActionLoading(prev => ({ ...prev, [providerId]: null }));
  };

  const toggleExpand = async (providerId) => {
    if (expandedProvider === providerId) {
      setExpandedProvider(null);
      return;
    }
    setExpandedProvider(providerId);
    if (!providerData[providerId]) {
      try {
        const res = await fetch(`${API}/accounting/data/${providerId}`, { headers });
        if (res.ok) {
          const d = await res.json();
          setProviderData(prev => ({ ...prev, [providerId]: d }));
        }
      } catch (e) { /* ignore */ }
    }
  };

  const handleDeleteData = async (providerId) => {
    setConfirmDelete(null);
    setActionLoading(prev => ({ ...prev, [providerId || '_all']: 'delete' }));
    try {
      const url = providerId ? `${API}/accounting/data/${providerId}` : `${API}/accounting/data`;
      const res = await fetch(url, { method: 'DELETE', headers });
      if (res.ok) {
        const d = await res.json();
        showToast(`${t.deleteSuccess} — ${d.records_removed} ${t.records}`);
        if (providerId) {
          setProviderData(prev => { const n = { ...prev }; delete n[providerId]; return n; });
          setExpandedProvider(null);
        } else {
          setProviderData({});
          setExpandedProvider(null);
        }
        await fetchAll();
      }
    } catch (e) { showToast(e.message, 'error'); }
    setActionLoading(prev => ({ ...prev, [providerId || '_all']: null }));
  };

  const handleStatusChange = async (providerId, newStatus) => {
    setActionLoading(prev => ({ ...prev, [providerId]: 'status' }));
    try {
      const res = await fetch(`${API}/accounting/status`, {
        method: 'POST', headers, body: JSON.stringify({ provider_id: providerId, status: newStatus }),
      });
      if (res.ok) {
        showToast(newStatus === 'active' ? t.connectSuccess : (newStatus === 'paused' ? t.paused : t.disabled));
        await fetchAll();
      }
    } catch (e) { showToast(e.message, 'error'); }
    setActionLoading(prev => ({ ...prev, [providerId]: null }));
  };

  const mustHave = (Array.isArray(providers) ? providers : []).filter(p => p.category === 'must_have');
  const enterprise = (Array.isArray(providers) ? providers : []).filter(p => p.category === 'enterprise');

  if (loading) {
    return (
      <div className="tool-page" data-testid="accounting-page">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
          <Loader className="spin" size={32} />
        </div>
      </div>
    );
  }

  const connectedCount = (Array.isArray(providers) ? providers : []).filter(p => p.status === 'active' || p.status === 'connected' || p.status === 'paused').length;

  return (
    <div className="tool-page" data-testid="accounting-page">
      {toast && (
        <div className={`acct-toast ${toast.type}`} data-testid="accounting-toast">
          {toast.type === 'success' ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
          {toast.msg}
        </div>
      )}

      <div className="tool-header">
        <button className="back-btn" onClick={() => navigate('/portal/ai-tools')} data-testid="accounting-back-btn">
          <ArrowLeft size={18} /> {t.back}
        </button>
      </div>

      <div className="acct-hero" data-testid="accounting-hero">
        <div className="acct-hero-content">
          <Building2 size={36} strokeWidth={1.5} />
          <div>
            <h1 className="heading-1">{t.title}</h1>
            <p className="body-large text-secondary">{t.subtitle}</p>
          </div>
        </div>
      </div>

      {/* Financial Summary Banner */}
      {connectedCount > 0 && summary?.financials && (
        <div className="acct-summary" data-testid="financial-summary">
          <div className="acct-summary-header">
            <h3>{t.summaryTitle}</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              {storage && storage.total_records > 0 && (
                <span className="acct-storage-badge" data-testid="storage-info">
                  <HardDrive size={12} />
                  {storage.total_records} {t.records} ({storage.estimated_size_kb} KB)
                  <span className="acct-ttl-hint">{t.autoCleanup} {storage.auto_cleanup_hours} {t.hours}</span>
                </span>
              )}
              <span className="acct-badge connected">{connectedCount} {t.connectedProviders}</span>
            </div>
          </div>
          <div className="acct-kpi-grid">
            <KpiCard icon={<TrendingUp size={20} />} label={t.revenue} value={fmt(summary.financials.total_revenue)} color="var(--color-success, #22c55e)" />
            <KpiCard icon={<TrendingDown size={20} />} label={t.totalExpenses} value={fmt(summary.financials.total_expenses)} color="var(--color-warning, #f59e0b)" />
            <KpiCard icon={<DollarSign size={20} />} label={t.netProfit} value={fmt(summary.financials.net_profit)} color={summary.financials.net_profit >= 0 ? 'var(--color-success, #22c55e)' : 'var(--color-danger, #ef4444)'} />
            <KpiCard icon={<Landmark size={20} />} label={t.bankBalance} value={fmt(summary.financials.bank_balance)} color="var(--color-primary, #3b82f6)" />
          </div>
          {storage && storage.total_records > 0 && (
            <div className="acct-summary-actions">
              <button className="acct-btn delete-all" onClick={() => setConfirmDelete('all')} data-testid="delete-all-data">
                <Trash2 size={14} /> {t.deleteAllData}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Confirm Delete Modal */}
      {confirmDelete && (
        <div className="acct-modal-overlay" data-testid="delete-confirm-modal">
          <div className="acct-modal">
            <ShieldAlert size={28} className="acct-modal-icon" />
            <h3>{confirmDelete === 'all' ? t.deleteConfirm : t.deleteProviderConfirm}</h3>
            <p className="acct-modal-sub">
              {confirmDelete === 'all'
                ? (lang === 'nl' ? 'Alle gesynchroniseerde boekhouddata wordt permanent verwijderd. Je koppelingen blijven actief.' : 'All synced accounting data will be permanently removed. Your connections will remain active.')
                : (lang === 'nl' ? 'De gesynchroniseerde data voor deze provider wordt verwijderd. Je koppeling blijft actief.' : 'Synced data for this provider will be removed. Your connection will remain active.')}
            </p>
            <div className="acct-modal-actions">
              <button className="acct-btn cancel" onClick={() => setConfirmDelete(null)} data-testid="delete-cancel">{t.cancel}</button>
              <button className="acct-btn confirm-delete" onClick={() => handleDeleteData(confirmDelete === 'all' ? null : confirmDelete)} data-testid="delete-confirm">{t.confirm}</button>
            </div>
          </div>
        </div>
      )}

      {/* Must-Have Providers */}
      <section className="acct-section" data-testid="must-have-providers">
        <h2 className="acct-section-title">
          <Zap size={18} /> {t.mustHave}
        </h2>
        <div className="acct-provider-grid">
          {(Array.isArray(mustHave) ? mustHave : []).map(p => (
            <ProviderCard
              key={p.id} provider={p} t={t} lang={lang}
              actionLoading={actionLoading[p.id]}
              expanded={expandedProvider === p.id}
              data={providerData[p.id]}
              onConnect={() => handleConnect(p.id)}
              onDisconnect={() => handleDisconnect(p.id)}
              onSync={() => handleSync(p.id)}
              onToggleExpand={() => toggleExpand(p.id)}
              onDeleteData={() => setConfirmDelete(p.id)}
              onStatusChange={(s) => handleStatusChange(p.id, s)}
            />
          ))}
        </div>
      </section>

      {/* Enterprise Providers */}
      <section className="acct-section" data-testid="enterprise-providers">
        <h2 className="acct-section-title">
          <Building2 size={18} /> {t.enterprise}
        </h2>
        <div className="acct-provider-grid">
          {(Array.isArray(enterprise) ? enterprise : []).map(p => (
            <ProviderCard
              key={p.id} provider={p} t={t} lang={lang}
              actionLoading={actionLoading[p.id]}
              expanded={expandedProvider === p.id}
              data={providerData[p.id]}
              onConnect={() => handleConnect(p.id)}
              onDisconnect={() => handleDisconnect(p.id)}
              onSync={() => handleSync(p.id)}
              onToggleExpand={() => toggleExpand(p.id)}
              onDeleteData={() => setConfirmDelete(p.id)}
              onStatusChange={(s) => handleStatusChange(p.id, s)}
            />
          ))}
        </div>
      </section>
    </div>
  );
}

function ProviderCard({ provider, t, lang, actionLoading, expanded, data, onConnect, onDisconnect, onSync, onToggleExpand, onDeleteData, onStatusChange }) {
  const p = provider;
  const desc = lang === 'nl' ? p.description_nl : p.description_en;
  const isActive = p.status === 'active' || p.status === 'connected';
  const isPaused = p.status === 'paused';
  const isDisabled = p.status === 'disabled';
  const isConnected = isActive || isPaused || isDisabled; // has a connection at all

  const statusColor = isActive ? '#22c55e' : isPaused ? '#f59e0b' : isDisabled ? '#ef4444' : '#9ca3af';
  const statusLabel = isActive ? t.connected : isPaused ? t.paused : isDisabled ? t.disabled : t.disconnected;

  return (
    <div className={`acct-card ${isActive ? 'connected' : isPaused ? 'paused' : ''}`} data-testid={`provider-card-${p.id}`}>
      <div className="acct-card-top">
        <div className="acct-card-logo" style={{ background: p.color + '18', color: p.color }}>
          {PROVIDER_ICONS[p.id] || p.name[0]}
        </div>
        <div className="acct-card-info">
          <div className="acct-card-name">
            {p.name}
            <a href={p.website} target="_blank" rel="noopener noreferrer" className="acct-ext-link">
              <ExternalLink size={12} />
            </a>
          </div>
          <div className="acct-card-desc">{desc}</div>
        </div>
        <div className="acct-status-dot-wrap" data-testid={`provider-status-${p.id}`}>
          <span className="acct-status-dot" style={{ background: statusColor }} />
          <span className="acct-status-label" style={{ color: statusColor }}>{statusLabel}</span>
        </div>
      </div>

      {/* Features */}
      <div className="acct-features">
        {p.features?.map(f => (
          <span key={f} className="acct-feature-tag">{t.category[f] || f}</span>
        ))}
      </div>

      {/* Actions */}
      <div className="acct-card-actions">
        {!isConnected ? (
          <button className="acct-btn connect" onClick={onConnect} disabled={!!actionLoading} data-testid={`connect-${p.id}`}>
            {actionLoading === 'connect' ? <Loader size={14} className="spin" /> : <Link2 size={14} />}
            {t.connect}
          </button>
        ) : (
          <>
            {isActive && (
              <button className="acct-btn sync" onClick={onSync} disabled={!!actionLoading} data-testid={`sync-${p.id}`}>
                {actionLoading === 'sync' ? <Loader size={14} className="spin" /> : <RefreshCw size={14} />}
                {actionLoading === 'sync' ? t.syncing : t.sync}
              </button>
            )}
            {isActive && (
              <button className="acct-btn pause" onClick={() => onStatusChange('paused')} disabled={!!actionLoading} data-testid={`pause-${p.id}`}>
                <Pause size={14} /> {t.pause}
              </button>
            )}
            {isPaused && (
              <button className="acct-btn resume" onClick={() => onStatusChange('active')} disabled={!!actionLoading} data-testid={`resume-${p.id}`}>
                <Play size={14} /> {t.resume}
              </button>
            )}
            {isDisabled && (
              <button className="acct-btn resume" onClick={() => onStatusChange('active')} disabled={!!actionLoading} data-testid={`activate-${p.id}`}>
                <Play size={14} /> {t.activate}
              </button>
            )}
            {(isActive || isPaused) && (
              <button className="acct-btn disable" onClick={() => onStatusChange('disabled')} disabled={!!actionLoading} data-testid={`disable-${p.id}`}>
                <Ban size={14} /> {t.disable}
              </button>
            )}
            <button className="acct-btn disconnect" onClick={onDisconnect} disabled={!!actionLoading} data-testid={`disconnect-${p.id}`}>
              <Unlink size={14} /> {t.disconnect}
            </button>
          </>
        )}
      </div>

      {/* Last sync */}
      {isConnected && (
        <div className="acct-last-sync">
          <Clock size={12} />
          {t.lastSync}: {p.last_sync ? new Date(p.last_sync).toLocaleString(lang === 'nl' ? 'nl-NL' : 'en-US') : t.never}
          {isActive && <span className="acct-ephemeral-note">{t.ephemeralNote}</span>}
        </div>
      )}

      {/* Data summary */}
      {isConnected && p.data_summary && (
        <div className="acct-data-summary">
          <div className="acct-data-row">
            <span><FileText size={13} /> {t.invoices}: <strong>{p.data_summary.invoices}</strong></span>
            <span><Receipt size={13} /> {t.expenses}: <strong>{p.data_summary.expenses}</strong></span>
          </div>
          <div className="acct-data-row">
            <span>{t.revenue}: <strong>{fmt(p.data_summary.total_revenue)}</strong></span>
            <span>{t.netProfit}: <strong style={{ color: p.data_summary.net_profit >= 0 ? '#22c55e' : '#ef4444' }}>{fmt(p.data_summary.net_profit)}</strong></span>
          </div>
          <div className="acct-data-summary-actions">
            <button className="acct-expand-btn" onClick={onToggleExpand} data-testid={`expand-${p.id}`}>
              {expanded ? <><ChevronUp size={14} /> {t.hideData}</> : <><ChevronDown size={14} /> {t.viewData}</>}
            </button>
            <button className="acct-expand-btn delete" onClick={onDeleteData} data-testid={`delete-data-${p.id}`}>
              <Trash2 size={13} /> {t.deleteData}
            </button>
          </div>
        </div>
      )}

      {/* Expanded data table */}
      {expanded && data && (
        <DataTable data={data} t={t} lang={lang} />
      )}
    </div>
  );
}

function DataTable({ data, t, lang }) {
  const [filter, setFilter] = useState('all');
  const items = data.data || [];
  const filtered = filter === 'all' ? items : (Array.isArray(items) ? items : []).filter(d => d.type === filter);

  const invoiceStats = (Array.isArray(items) ? items : []).filter(d => d.type === 'invoice');
  const paid = (Array.isArray(invoiceStats) ? invoiceStats : []).filter(d => d.status === 'paid').length;
  const pending = (Array.isArray(invoiceStats) ? invoiceStats : []).filter(d => d.status === 'pending').length;
  const overdue = (Array.isArray(invoiceStats) ? invoiceStats : []).filter(d => d.status === 'overdue').length;

  return (
    <div className="acct-data-detail" data-testid="accounting-data-table">
      <div className="acct-data-filters">
        {['all', 'invoice', 'expense', 'bank'].map(f => (
          <button key={f} className={`acct-filter-btn ${filter === f ? 'active' : ''}`} onClick={() => setFilter(f)}>
            {f === 'all' ? (lang === 'nl' ? 'Alles' : 'All') :
             f === 'invoice' ? t.invoices :
             f === 'expense' ? t.expenses :
             (lang === 'nl' ? 'Bank' : 'Bank')}
          </button>
        ))}
      </div>

      {filter === 'all' && invoiceStats.length > 0 && (
        <div className="acct-invoice-stats">
          <span className="acct-stat paid"><CheckCircle size={12} /> {paid} {t.paid}</span>
          <span className="acct-stat pending"><Clock size={12} /> {pending} {t.pending}</span>
          <span className="acct-stat overdue"><AlertTriangle size={12} /> {overdue} {t.overdue}</span>
        </div>
      )}

      <div className="acct-table-wrap">
        <table className="acct-table">
          <thead>
            <tr>
              <th>{lang === 'nl' ? 'Datum' : 'Date'}</th>
              <th>{lang === 'nl' ? 'Type' : 'Type'}</th>
              <th>{lang === 'nl' ? 'Omschrijving' : 'Description'}</th>
              <th>{lang === 'nl' ? 'Bedrag' : 'Amount'}</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 30).map((d, i) => (
              <tr key={i}>
                <td>{d.date}</td>
                <td>
                  <span className={`acct-type-badge ${d.type}`}>
                    {d.type === 'invoice' ? t.invoices : d.type === 'expense' ? t.expenses : 'Bank'}
                  </span>
                </td>
                <td>{d.description}</td>
                <td className={`acct-amount ${d.type}`}>
                  {d.type === 'bank' ? fmt(d.balance) : fmt(d.amount)}
                </td>
                <td>
                  {d.status && <span className={`acct-status-badge ${d.status}`}>{d.status}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length > 30 && (
          <div className="acct-more">+{filtered.length - 30} {lang === 'nl' ? 'meer' : 'more'}</div>
        )}
      </div>
    </div>
  );
}

function KpiCard({ icon, label, value, color }) {
  return (
    <div className="acct-kpi">
      <div className="acct-kpi-icon" style={{ color }}>{icon}</div>
      <div className="acct-kpi-label">{label}</div>
      <div className="acct-kpi-value" style={{ color }}>{value}</div>
    </div>
  );
}

function fmt(v) {
  if (v == null) return '-';
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(v);
}
